/**
 * ============================================================================
 * useEquipmentQuery — Phase 6e frontend (Equipment & Validation Repository).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for the Equipment Workbench list. Server
 *          returns the per-equipment summary plus a `stats` envelope for the
 *          four hero KPIs (Total Qualified, Restricted Count, Cal Due This
 *          Week, Requal Due This Month).
 * Endpoint: GET /api/v1/equipment
 *
 * NOTE on `payload`: per-equipment opaque extras (warranty info, custom
 *  category tags, vendor portal links, etc.) come back as a JSON map. We
 *  narrow at the call site rather than weakening the public type to `any`.
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export type EquipmentCriticality = 'CRITICAL' | 'MAJOR' | 'MINOR' | 'NON_GMP';

export type GampClass = 'GAMP1' | 'GAMP3' | 'GAMP4' | 'GAMP5';

export type EquipmentLifecycleStatus =
  | 'PLANNED'
  | 'URS_APPROVED'
  | 'DQ_APPROVED'
  | 'FAT_PASSED'
  | 'SAT_PASSED'
  | 'IQ_PASSED'
  | 'OQ_PASSED'
  | 'PQ_PASSED'
  | 'QUALIFIED'
  | 'RESTRICTED'
  | 'RETIRED'
  | 'DECOMMISSIONED';

export interface EquipmentSummary {
  equipment_id: string;
  equipment_tag: string;
  name: string;
  manufacturer: string | null;
  model: string | null;
  criticality: EquipmentCriticality;
  gamp_class: GampClass;
  lifecycle_status: EquipmentLifecycleStatus;
  site_code: string | null;
  location: string | null;
  next_calibration_at_utc: string | null;
  next_pm_at_utc: string | null;
  next_requalification_at_utc: string | null;
  last_calibration_at_utc: string | null;
  /** Opaque per-equipment extras; narrow before use. */
  payload: Record<string, unknown>;
}

export interface EquipmentWorkbenchStats {
  total_qualified: number;
  restricted_count: number;
  cal_due_this_week: number;
  requal_due_this_month: number;
}

export interface EquipmentResponse {
  items: EquipmentSummary[];
  stats: EquipmentWorkbenchStats;
  total: number;
}

export interface EquipmentQueryParams {
  criticality?: EquipmentCriticality;
  gamp_class?: GampClass;
  lifecycle_status?: EquipmentLifecycleStatus;
  site_code?: string;
  q?: string;
}

export function useEquipmentQuery(
  params: EquipmentQueryParams,
): UseQueryResult<EquipmentResponse> {
  return useQuery<EquipmentResponse>({
    queryKey: ['equipment', 'workbench', params],
    queryFn: ({ signal }) =>
      apiFetch<EquipmentResponse>('/api/v1/equipment', {
        query: {
          criticality: params.criticality,
          gamp_class: params.gamp_class,
          lifecycle_status: params.lifecycle_status,
          site_code: params.site_code,
          q: params.q,
        },
        signal,
      }),
  });
}
